/**
 * SupplierProjectSelectorPage.jsx
 * ────────────────────────────────
 * Screen 1 — Entry point for supplier operators.
 * Route: /supplier/disposal
 * Shows project cards filtered by status and OEM.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { getSupplierProjects, checkFrostDismissal, dismissFrost } from '../../api/supplierDisposalApi';
import { useDisposalOfflineQueue } from '../../hooks/useDisposalOfflineQueue';
import SupplierLayout from '../../layouts/SupplierLayout';
import FrostDismissalBanner from '../../components/supplier/FrostDismissalBanner';
import './SupplierProjectSelectorPage.css';

const FROST_STATES = new Set([
  'idaho', 'illinois', 'indiana', 'iowa', 'maine', 'michigan',
  'minnesota', 'montana', 'new hampshire', 'north dakota', 'ohio',
  'pennsylvania', 'south dakota', 'vermont', 'wisconsin', 'wyoming',
  'id', 'il', 'in', 'ia', 'me', 'mi', 'mn', 'mt', 'nh', 'nd',
  'oh', 'pa', 'sd', 'vt', 'wi', 'wy',
]);

function isFrostSeason() {
  const month = new Date().getMonth() + 1; // 1-12
  return month >= 2 && month <= 6;
}

function statusBadgeClass(status) {
  const s = (status || '').toUpperCase();
  if (s === 'READY') return 'sp-badge-green';
  if (s === 'IN_PROGRESS') return 'sp-badge-amber';
  return 'sp-badge-grey';
}

export default function SupplierProjectSelectorPage() {
  const { callerEmail } = useUser();
  const navigate = useNavigate();

  // Auto-drain on mount + connectivity restore (no explicit drain in fetch flow)
  const { getDisposalQueueStatus, drainDisposalQueue } = useDisposalOfflineQueue(callerEmail);

  const [projects, setProjects] = useState([]);
  const [failedCount, setFailedCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [oemFilter, setOemFilter] = useState('');

  // Frost banner state
  const [frostVisible, setFrostVisible] = useState(false);
  const [frostState, setFrostState] = useState('');
  const [pendingProject, setPendingProject] = useState(null);

  // Check for failed queue items on mount
  useEffect(() => {
    let cancelled = false;
    getDisposalQueueStatus().then(status => {
      if (!cancelled) setFailedCount(status.failed ?? 0);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [getDisposalQueueStatus]);

  // Load projects
  useEffect(() => {
    if (!callerEmail) return;
    setLoading(true);
    setError(null);
    const fetchData = async () => {
      try {
        const data = await getSupplierProjects(callerEmail);
        const list = Array.isArray(data) ? data : data.projects || data.results || [];
        setProjects(list);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [callerEmail]);

  // Unique OEMs for filter
  const oemOptions = useMemo(() => {
    const set = new Set();
    projects.forEach(p => { if (p.oem) set.add(p.oem); });
    return [...set].sort();
  }, [projects]);

  // Filtered projects
  const filtered = useMemo(() => {
    return projects.filter(p => {
      const s = (p.status || '').toUpperCase();
      if (statusFilter === 'ACTIVE' && s !== 'READY' && s !== 'IN_PROGRESS') return false;
      if (statusFilter === 'CLOSED' && s !== 'CLOSED') return false;
      if (oemFilter && p.oem !== oemFilter) return false;
      return true;
    });
  }, [projects, statusFilter, oemFilter]);

  // Site name from first project
  const siteName = projects[0]?.site_name || '';

  const navigateToProject = useCallback((proj) => {
    const pid = proj.project_id;
    // Check for existing roll-off session
    try {
      const roRaw = sessionStorage.getItem(`tmg_rolloff_session_${pid}`);
      if (roRaw) {
        const roSession = JSON.parse(roRaw);
        if (roSession?.activeSession?.bolId) {
          navigate(`/supplier/disposal/${pid}/rolloff/${roSession.activeSession.bolId}`);
          return;
        }
      }
    } catch { /* ignore */ }
    // Check for existing flatbed session
    try {
      const fbRaw = sessionStorage.getItem(`tmg_loading_session_${pid}`);
      if (fbRaw) {
        const fbSession = JSON.parse(fbRaw);
        if (fbSession?.activeSession?.bolId) {
          navigate(`/supplier/disposal/${pid}/load/${fbSession.activeSession.bolId}`);
          return;
        }
      }
    } catch { /* ignore */ }
    // Default: flatbed loading
    navigate(`/supplier/disposal/${pid}/load`);
  }, [navigate]);

  // Handle project card tap
  const handleProjectTap = useCallback(async (project) => {
    const s = (project.status || '').toUpperCase();
    if (s === 'CLOSED') return;

    const siteState = (project.site_state || project.site_state_province || '').trim().toLowerCase();

    if (isFrostSeason() && FROST_STATES.has(siteState)) {
      try {
        const result = await checkFrostDismissal(callerEmail, project.project_id);
        if (!result.dismissed) {
          setFrostState(project.site_state || project.site_state_province || 'this state');
          setPendingProject(project);
          setFrostVisible(true);
          return;
        }
      } catch {
        // If frost check fails, proceed anyway — non-blocking
      }
    }

    navigateToProject(project);
  }, [callerEmail, navigateToProject]);

  // Frost dismiss handler
  async function handleFrostDismiss() {
    if (pendingProject) {
      try {
        await dismissFrost(callerEmail, pendingProject.project_id, pendingProject.site_id);
      } catch {
        // Non-blocking — proceed even if dismiss write fails
      }
      navigateToProject(pendingProject);
    }
    setFrostVisible(false);
    setPendingProject(null);
  }

  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      await drainDisposalQueue(callerEmail);
      const status = await getDisposalQueueStatus();
      setFailedCount(status.failed ?? 0);
    } catch {
      const status = await getDisposalQueueStatus();
      setFailedCount(status.failed ?? 0);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <SupplierLayout>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="sp-header">
        <span className="sp-header-site">{siteName || 'Loading...'}</span>
        <span className="sp-header-label">Disposal Portal</span>
        <span className="sp-header-label">OEM Projects</span>
      </div>

      {/* ── Failed queue banner ─────────────────────────────────────── */}
      {failedCount > 0 && (
        <div className="sps-failed-banner">
          <span className="sps-failed-banner__message">
            ⚠️ {failedCount} shipment{failedCount !== 1 ? 's' : ''} failed to sync.
          </span>
          <button
            className="sps-failed-banner__retry-btn"
            onClick={handleRetryFailed}
            disabled={retrying}
          >
            {retrying ? 'Retrying…' : 'Tap to retry'}
          </button>
        </div>
      )}

      {/* ── Filter bar ────────────────────────────────────────────────── */}
      <div className="sp-filters">
        <div className="sp-status-group">
          {['ACTIVE', 'ALL', 'CLOSED'].map(v => (
            <button key={v}
              className={`sp-filter-btn ${statusFilter === v ? 'active' : ''}`}
              onClick={() => setStatusFilter(v)}>
              {v}
            </button>
          ))}
        </div>
        <select className="sp-oem-select" value={oemFilter}
          onChange={e => setOemFilter(e.target.value)}>
          <option value="">All OEMs</option>
          {oemOptions.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {(statusFilter !== 'ACTIVE' || oemFilter) && (
          <button className="sp-clear-btn"
            onClick={() => { setStatusFilter('ACTIVE'); setOemFilter(''); }}>
            Clear
          </button>
        )}
      </div>

      {/* ── Frost banner ──────────────────────────────────────────────── */}
      <FrostDismissalBanner
        isVisible={frostVisible}
        stateName={frostState}
        onDismiss={handleFrostDismiss}
      />

      {/* ── Content ───────────────────────────────────────────────────── */}
      <div className="sp-content">
        {loading ? (
          <div className="sp-state">Loading projects...</div>
        ) : error ? (
          <div className="sp-state sp-state-error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="sp-state">No active projects for your site.</div>
        ) : (
          <div className="sp-cards">
            {filtered.map(p => {
              const s = (p.status || '').toUpperCase();
              const isClosed = s === 'CLOSED';
              // TODO Phase 5: enrich with per-project shipped_count from PROJECT_STATS mode
              const target = p.target_tool_count || 0;
              const shipped = p.shipped_tool_count || 0;
              const balance = target - shipped;
              const pct = target > 0 ? Math.round((shipped / target) * 100) : 0;

              return (
                <div
                  key={p.project_id}
                  className={`sp-card ${isClosed ? 'sp-card-closed' : ''}`}
                  onClick={() => !isClosed && handleProjectTap(p)}
                >
                  {/* Line 1 */}
                  <div className="sp-card-row1">
                    <span className="sp-card-oem">{p.oem || '—'}</span>
                    <span className="sp-card-pn">{p.project_number || '—'}</span>
                    <span className={`sp-card-badge ${statusBadgeClass(s)}`}>{s}</span>
                  </div>

                  {/* Line 2 */}
                  <div className="sp-card-row2">
                    <span className="sp-card-counts">
                      Target: {target} &middot; Shipped: {shipped} &middot; Balance: {balance}
                    </span>
                  </div>

                  {/* Line 3 — progress */}
                  <div className="sp-card-progress-row">
                    <div className="sp-card-progress-track">
                      <div className="sp-card-progress-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <span className="sp-card-pct">{pct}%</span>
                  </div>

                  {/* Line 4 — ship log button */}
                  <div className="sp-card-row4">
                    <button className="sp-shiplog-btn" onClick={e => {
                      e.stopPropagation();
                      navigate(`/supplier/disposal/${p.project_id}/log`);
                    }}>
                      SHIP LOG
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SupplierLayout>
  );
}
